import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  Alert,
  Link as MuiLink,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { dashboardApi } from '@/core/api';
import type { ResponseDashboardByCustomerDTO } from '@/core/api';
import { fromAxiosError } from '@/core/api';
import { BackToTopFab, HelpAlert, usePageTitle } from '@/shared';
import ActivityBarChart from './ActivityBarChart';
import KpiCard from './KpiCard';
import KpiCardSkeleton from './KpiCardSkeleton';
import { kpiVisual, sectionEyebrowSx } from './kpiVisuals';
import KpiIcon from './KpiIcon';
import { DocumentRequestsTable } from '../document-requests';

export default function CustomerHome() {
  const { t, i18n } = useTranslation();
  usePageTitle('home.customer.pageTitle');

  const [data, setData] = useState<ResponseDashboardByCustomerDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setErrorMessage(null);

    async function fetchDashboard() {
      try {
        const result = await dashboardApi.getCustomerDashboard();
        if (!cancelled) {
          setData(result);
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMessage(fromAxiosError(err).message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void fetchDashboard();

    return () => {
      cancelled = true;
    };
  }, [version]);

  if (errorMessage !== null) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <strong>{t('home.errorTitle')}: </strong>
          {errorMessage}
        </Alert>
      </Box>
    );
  }

  const ns = 'HOME.DASHBOARD.CUSTOMER';

  // Weekday short names in the active locale — matches the admin chart's axis.
  const dayLabelFormatter = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' });
  const claimActivity = (data?.claimActivity ?? []).map((entry) => ({
    label: dayLabelFormatter.format(new Date(entry.date)),
    count: entry.count,
  }));

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header — sticky so title and refresh controls stay accessible on scroll */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          bgcolor: 'background.default',
          zIndex: 1,
          py: 1,
          mb: 2,
        }}
      >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" component="h1" sx={{ color: 'primary.main' }}>
          {t('home.customer.pageTitle')}
        </Typography>
        <Tooltip title={t('actions.refresh')}>
          <span>
            <IconButton
              aria-label={t('actions.refresh')}
              size="small"
              onClick={() => setVersion((v) => v + 1)}
              disabled={loading}
              sx={{ '@media print': { display: 'none' } }}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      </Box>

      <HelpAlert
        persistKey="home.customer.welcome"
        severity="info"
        variant="outlined"
        title={t('HELP.home.customer.welcome.title')}
      >
        {t('HELP.home.customer.welcome.body')}
      </HelpAlert>

      {/* ------------------------------------------------------------------ */}
      {/* Section 1 — Outstanding, all time (4 KPI cards)                    */}
      {/* DTO field source: data.main — i18n key namespace: last24Hours      */}
      {/*                                                                    */}
      {/* The i18n namespace still reads `last24Hours` because renaming it   */}
      {/* would touch every catalog for no user-visible gain; the strings    */}
      {/* under it no longer mention 24 hours. These tiles now count all     */}
      {/* time — an open claim does not stop being open because it was filed */}
      {/* the day before yesterday, and the old window hid most of the       */}
      {/* customer's real workload.                                          */}
      {/* ------------------------------------------------------------------ */}
      <Typography
        id="customer-home-last24-title"
        component="h2"
        sx={sectionEyebrowSx}
      >
        {t(`${ns}.last24Hours.title`)}
      </Typography>
      <Grid
        container
        spacing={2}
        sx={{ mb: 4 }}
        aria-busy={loading || !data}
        role="region"
        aria-labelledby="customer-home-last24-title"
      >
        {loading || !data
          ? Array.from({ length: 4 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <KpiCardSkeleton />
              </Grid>
            ))
          : (
            [
              // "Pending Document Request (last 7 days)" used to sit here and
              // was removed. Every other tile in this row counts the last 24
              // hours, so a 7-day tile under a LAST 24 HOURS heading read as a
              // contradiction — pending 0 next to pending 54. The field is
              // still returned by the API and simply not shown.
              // The two document tiles carry tooltips because OPEN and PENDING
              // are not two flavours of the same thing and the old labels made
              // them look like it. OPEN is a slot on the claim's checklist that
              // nobody has asked for yet; PENDING is a document an adjuster has
              // actually requested (ClaimDocumentRequest stamps requestDate and
              // starts the reminder job) and is waiting on. Only PENDING is the
              // customer's move.
              // Every tile now lands somewhere that shows the rows it counted.
              // They were all inert: a customer could be told they owed 88
              // documents with no way to see which, which is the same fault as
              // the provider tiles earlier — a number is where a question
              // starts, not where it ends.
              { key: 'unreadMessages',         value: data.main.unreadMessages,         helpText: undefined, linkTo: '/catalogs/administrator-chat' },
              { key: 'openClaims',             value: data.main.openClaims,             helpText: undefined, linkTo: '/claims?status=OPEN' },
              { key: 'openDocumentRequest',    value: data.main.openDocumentRequest,    helpText: t(`${ns}.last24Hours.openDocumentRequestHelp`),    linkTo: '/document-requests?status=OPEN' },
              { key: 'pendingDocumentRequest', value: data.main.pendingDocumentRequest, helpText: t(`${ns}.last24Hours.pendingDocumentRequestHelp`), linkTo: '/document-requests?status=PENDING' },
            ] as const
          ).map(({ key, value, helpText, linkTo }) => (
            <Grid item xs={12} sm={6} md={3} key={key}>
              <KpiCard
                helpText={helpText}
                label={t(`${ns}.last24Hours.${key}`)}
                value={value}
                icon={<KpiIcon kpiKey={key} />}
                tone={kpiVisual(key).tone}
                linkTo={linkTo}
              />
            </Grid>
          ))
        }
      </Grid>

      {/* The "Last Week" row lived here and was dropped. A fleet customer may
          need a tow once a year — for most of them every tile in a 7-day row
          reads zero, which makes the dashboard look broken rather than quiet.
          The counters it held (new/closed claims, tow requests, roadside) are
          still returned on `data.week` and simply not shown; the Outstanding
          row above and the documents panel below are the things a customer can
          actually act on. */}


      {/* ------------------------------------------------------------------ */}
      {/* ------------------------------------------------------------------ */}
      {/* Documents you owe — the outstanding work, above the trend chart.    */}
      {/*                                                                    */}
      {/* Placed before Claims Filed on purpose: this is the only thing on    */}
      {/* the page the customer can act on today, and the chart is a look     */}
      {/* back. PENDING only — an OPEN row has not been asked of them yet.    */}
      {/* ------------------------------------------------------------------ */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 'medium' }}>
            {t('DOCUMENT-REQUESTS.owedPanel.title')}
          </Typography>
          <MuiLink component={RouterLink} to="/document-requests?status=PENDING" variant="body2">
            {t('DOCUMENT-REQUESTS.owedPanel.viewAll')}
          </MuiLink>
        </Box>
        <DocumentRequestsTable
          status="PENDING"
          rowLimit={8}
          emptyTitle={t('DOCUMENT-REQUESTS.empty.owed')}
        />
      </Paper>

      {/* Claims filed over the last 7 days. The dashboard was point-in-time  */}
      {/* counters only, with no sense of trend.                              */}
      {/* Rendered only when the wire carries the series, so an older API     */}
      {/* revision degrades to no chart rather than an empty axis.            */}
      {/* ------------------------------------------------------------------ */}
      {claimActivity.length > 0 && (
        <ActivityBarChart
          titleId="customer-home-claim-activity-title"
          title={t('CUSTOMER-HOME.claimActivity.title')}
          helpText={t('CUSTOMER-HOME.claimActivity.help')}
          helpAriaLabel={t('CUSTOMER-HOME.claimActivity.ariaLabel')}
          data={claimActivity}
          loading={loading || !data}
        />
      )}
      <BackToTopFab />
    </Box>
  );
}
